/**
 * AgentLeadsDrawer — slide-in panel on the Company Leads Dashboard.
 *
 * Opens when a company admin clicks an agent's row in the Agent Performance
 * tab. Shows that agent's leads using the **exact same** filter + table +
 * pagination + mobile-Accordion UX as the standalone /app/leads page —
 * because it embeds LeadsTableSection with a bound `fetchFn`.
 *
 * Pure PrimeReact Sidebar — native header (agent banner) with its built-in
 * close icon, native dismissable mask + ESC, and the native
 * `.p-sidebar-content` scroll region. The embedded list renders at natural
 * height (tableScrollable=false) so there is exactly one scroll region (the
 * Sidebar's own content). No custom CSS, no custom scroll handling.
 *
 * Always position="right" (its content scrolls reliably). position="full"
 * has a known mobile scroll bug where the panel outgrows the viewport and
 * the background scrolls instead — so on mobile we keep the same
 * right-drawer code path and just go full-width.
 *
 * Layout:
 *   • Desktop (≥ md): right Sidebar, width min(1100px, 90vw)
 *   • Mobile  (< md): right Sidebar, width 100vw
 *
 * The drawer owns no list state — everything lives in LeadsTableSection.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Avatar } from 'primereact/avatar';

import LeadsTableSection from '../OfferCalculation/components/LeadsTableSection';
import { getAgentLeads } from '../../services/companyLeadsDashboardService';

const initialsFrom = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/).filter(Boolean).slice(0, 2);
    return parts.map((w) => w[0]).join('').toUpperCase() || '?';
};

const AgentLeadsDrawer = ({ visible, agent, onHide }) => {
    const [totalRecords, setTotalRecords] = useState(null);

    // Agent id is stable across re-renders even though `agent` is a fresh
    // object reference each time the parent re-renders.
    const agentId = agent?.agent_id || agent?.id || null;

    // Bind the agent id into a stable fetcher so LeadsTableSection's
    // useCallback dep stays stable and we don't re-fetch on every render.
    const fetchAgentLeads = useCallback(
        (params) => getAgentLeads(agentId, params),
        [agentId],
    );

    // Memoise the totals callback for the same reason.
    const onTotal = useMemo(() => setTotalRecords, []);

    // No agent → render nothing (and avoid the network call on the embedded section).
    if (!agent) return null;

    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 991.98px)').matches;

    return (
        <Sidebar
            visible={visible}
            onHide={onHide}
            /* Always position="right" — its .p-sidebar-content scrolls
               reliably (that's why desktop works). position="full" has a
               known mobile scroll bug (panel outgrows the viewport, the
               background scrolls instead). On mobile we keep the SAME
               right-drawer code path, just full-width. Identical behavior
               to desktop, just wider. */
            position="right"
            style={{ width: isMobile ? '100vw' : 'min(1100px, 90vw)' }}
            blockScroll
            dismissable
            closeOnEscape
            header={
                <div className="flex align-items-center gap-2 min-w-0">
                    <Avatar
                        label={initialsFrom(agent.name)}
                        shape="circle"
                        size="large"
                        style={{ backgroundColor: 'var(--primary-color)', color: 'var(--primary-color-text)' }}
                    />
                    <div className="flex flex-column min-w-0">
                        <span className="text-900 font-semibold text-base white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {agent.name || 'Agent'}
                        </span>
                        <span className="text-500 text-xs">
                            {totalRecords === null
                                ? 'Loading leads…'
                                : `${totalRecords} lead${totalRecords === 1 ? '' : 's'} sourced by this agent`}
                        </span>
                    </div>
                </div>
            }
        >
            {/* PrimeReact's .p-sidebar-content already scrolls natively
                (overflow-y:auto in the theme) and the header above stays
                fixed. The only thing that broke scroll before was the
                embedded DataTable's scrollHeight="flex" collapsing — so we
                render it at natural height (tableScrollable=false) and the
                whole list scrolls inside the Sidebar's own content area.
                No custom CSS, no nested scroll regions. */}
            <LeadsTableSection
                fetchFn={fetchAgentLeads}
                syncToUrl={false}
                onTotalRecords={onTotal}
                wrapInCard={false}
                tableScrollable={false}
            />
        </Sidebar>
    );
};

export default AgentLeadsDrawer;
